import { useEffect, useState } from 'react'
import { api, type FotoRecebida } from '../api/cliente'
import { Erro } from './Basicos'
import { momento } from '../datas'
import { salvar } from '../salvar'

/**
 * As fotos de um quadro: a evidência que sustenta o laudo.
 *
 * **Elas vêm atrás de autenticação**, então não dá para apontar `<img src>` para a API — sem o
 * cabeçalho a resposta é 401. Cada uma é buscada com o token e vira um endereço de blob, revogado
 * ao fechar o painel. Sem revogar, abrir dez quadros seguidos deixaria dez cópias na memória da
 * aba até a página recarregar.
 *
 * **Em resolução plena**, porque é assim que elas foram guardadas e não há miniatura no servidor.
 * O painel abre sob demanda justamente por isso.
 */
export function FotosDoQuadro({ quadroId }: { quadroId: string }) {
  const [fotos, setFotos] = useState<FotoRecebida[] | null>(null)
  const [enderecos, setEnderecos] = useState<Record<string, string>>({})
  const [erro, setErro] = useState<unknown>(null)

  useEffect(() => {
    let vivo = true
    const criados: string[] = []

    async function busca() {
      const lista = await api.fotosDoQuadro(quadroId)
      if (!vivo) return
      setFotos(lista)

      // Uma de cada vez: são arquivos grandes, e quatro downloads simultâneos numa rede de
      // escritório só atrasam o primeiro, que é o que a pessoa está olhando.
      for (const foto of lista) {
        const { blob } = await api.baixarFoto(foto.id)
        if (!vivo) return
        const endereco = URL.createObjectURL(blob)
        criados.push(endereco)
        setEnderecos((antes) => ({ ...antes, [foto.id]: endereco }))
      }
    }

    busca().catch((falha: unknown) => vivo && setErro(falha))

    return () => {
      vivo = false
      criados.forEach(URL.revokeObjectURL)
    }
  }, [quadroId])

  if (erro) return <div className="fotos"><Erro erro={erro} /></div>
  if (!fotos) return <div className="fotos"><span className="girando" /> carregando as fotos…</div>

  return (
    <div className="fotos">
      {fotos.map((foto) => (
        <figure key={foto.id} className="foto">
          {enderecos[foto.id] ? (
            // Abrir em aba nova é o zoom: a foto tem resolução de sobra e o navegador já sabe
            // ampliar. Um visualizador aqui dentro seria código para refazer o que ele faz.
            <a href={enderecos[foto.id]} target="_blank" rel="noreferrer">
              <img src={enderecos[foto.id]} alt={`Foto ${foto.tipo} do quadro`} />
            </a>
          ) : (
            <div className="vazia">
              <span className="girando" />
            </div>
          )}
          <figcaption>
            <b>{foto.tipo}</b>
            <span>
              {foto.largura}×{foto.altura} · {megabytes(foto.tamanho)}
            </span>
            <span>{momento(foto.capturadaEm)}</span>
            <button
              className="btn g sm"
              disabled={!enderecos[foto.id]}
              onClick={() =>
                api.baixarFoto(foto.id).then(({ blob, nome }) => salvar(blob, nome))
              }
            >
              Baixar
            </button>
          </figcaption>
        </figure>
      ))}
    </div>
  )
}

function megabytes(bytes: number) {
  return bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
